import React, { useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Switch } from '../../ui/switch';

export const toImportance = (weight, n) => Math.max(1, Math.min(5, Math.round((weight || 0) * n * 3) || 3));
const TYPES = ['technical', 'behavioral', 'domain'];

const Row = ({ c, i, share, onChange, onRemove }) => (
  <div className="iv-card" style={{ marginBottom: 0, padding: '1rem' }} data-testid={`competency-row-${i}`}>
    <div style={{ display: 'grid', gap: '0.6rem', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
      <input className="iv-input" value={c.name} placeholder="Competency name" onChange={(e) => onChange({ name: e.target.value })} aria-label="Competency name" data-testid={`competency-name-${i}`} />
      <select className="iv-select" value={c.type} onChange={(e) => onChange({ type: e.target.value })} aria-label="Competency type" data-testid={`competency-type-${i}`}>
        {TYPES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
      </select>
    </div>
    <div className="iv-row" style={{ justifyContent: 'space-between', marginTop: '0.6rem' }}>
      <div className="iv-row" style={{ gap: '0.35rem' }} role="group" aria-label="Importance">
        <span className="iv-small">Importance</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} className={`iv-chip ${c.importance === n ? 'is-on' : ''}`} style={{ minWidth: 34 }} onClick={() => onChange({ importance: n })} data-testid={`competency-importance-${i}-${n}`}>{n}</button>
        ))}
        <span className="iv-small iv-mono" data-testid={`competency-share-${i}`}>{Math.round(share * 100)}%</span>
      </div>
      <div className="iv-row" style={{ gap: '0.5rem' }}>
        <label className="iv-row iv-small" style={{ gap: '0.4rem' }}>
          <Switch checked={!!c.must_have} onCheckedChange={(v) => onChange({ must_have: v })} data-testid={`competency-must-have-${i}`} /> Must-have
        </label>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={onRemove} aria-label="Remove competency" data-testid={`competency-remove-${i}`}><Trash2 size={14} /></button>
      </div>
    </div>
    <textarea className="iv-textarea" rows={2} style={{ marginTop: '0.6rem' }} value={c.evidence_needed} placeholder="What evidence counts? e.g. specific decisions, metrics, trade-offs"
      onChange={(e) => onChange({ evidence_needed: e.target.value })} aria-label="Evidence needed" data-testid={`competency-evidence-${i}`} />
  </div>
);

export default function CompetencyEditor({ comps, setComps }) {
  // Stable row ids (kept beside the data so nothing extra is saved) — removing a row never reuses another's id.
  const ids = useRef([]);
  const seq = useRef(0);
  while (ids.current.length < comps.length) ids.current.push(`c${seq.current++}`);
  ids.current.length = comps.length;
  const total = comps.reduce((a, c) => a + c.importance, 0) || 1;
  const update = (i, patch) => setComps(comps.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const add = () => setComps([...comps, { name: '', type: 'technical', importance: 3, must_have: false, evidence_needed: '' }]);
  return (
    <div data-testid="competency-editor" style={{ display: 'grid', gap: '0.6rem' }}>
      {comps.map((c, i) => (
        <Row key={ids.current[i]} c={c} i={i} share={c.importance / total} onChange={(p) => update(i, p)}
          onRemove={() => { ids.current.splice(i, 1); setComps(comps.filter((_, j) => j !== i)); }} />
      ))}
      <div>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" disabled={comps.length >= 12} onClick={add} data-testid="competency-add-btn"><Plus size={14} /> Add competency</button>
      </div>
    </div>
  );
}
